import {
  activityWorkflowStatusConstante,
  activityActiveWorkflowStatusesConstante,
  activityCompletionFilterConstante,
  activityChronologyFilterConstante,
  activityTemporalStatusConstante,
  activityCompletedWorkflowStatusesConstante,
} from "@/constantes/contractActivity";
import { formValidator } from "@/services/form/form-validator";

class ActivityViewModelManager {
  toActivityViewModel(evolution) {
    return {
      id: evolution.contractActivity?.id,
      evolutionId: evolution.id,
      name: evolution.contractActivity?.name,
      activityType: evolution.contractActivity?.activityType,
      icon: evolution.contractActivity?.icon,
      startDate: evolution.contractActivity?.startDate,
      endDate: evolution.contractActivity?.endDate,
      workflowStatus: evolution.contractActivity?.workflowStatus,
      temporalStatus: evolution.contractActivity?.temporalStatus,
      currentStep: evolution.contractActivity?.currentStep,
      levelOfAchievement: evolution.contractActivity?.levelOfAchievement ?? 0,
      skillsCount: evolution.contractActivity?.skillsCount ?? 0,
      description: evolution.contractActivity?.description,
      missions: evolution.contractActivity?.missions ?? [],
      commentaire: evolution.commentaire,
      hasHistory: !!evolution.hasHistory,
    };
  }

  // RG2.2V2.1 : statut workflow (axe complétude) et statut temporel (axe chronologie) sont deux
  // axes indépendants, jamais dérivés l'un de l'autre : ils sont donc filtrés séparément et
  // combinés en ET par matchesFilters(). Une activité brouillon dont la date de fin est future
  // correspond ainsi à la fois à "À compléter" et à "En cours", comme dans la maquette
  // (data-status="encours acompleter" porté simultanément par une même carte).
  // "Archivées" (CANCELLED) est en revanche exclusif des autres valeurs de complétude, à
  // l'identique du filtre miroir côté Mission (missionStatusFilterConstante.ARCHIVED) : une fois
  // annulée, une activité sort des vues actives (Toutes / À compléter / Complètes).
  matchesCompletionFilter(activity, completionFilter) {
    const isCancelled = activity.workflowStatus === activityWorkflowStatusConstante.CANCELLED;
    if (completionFilter === activityCompletionFilterConstante.ARCHIVED) {
      return isCancelled;
    }
    if (isCancelled) {
      return false;
    }
    if (completionFilter === activityCompletionFilterConstante.ALL) {
      return true;
    }
    if (completionFilter === activityCompletionFilterConstante.TO_COMPLETE) {
      return activity.workflowStatus === activityWorkflowStatusConstante.DRAFT;
    }
    if (completionFilter === activityCompletionFilterConstante.COMPLETED) {
      return activityCompletedWorkflowStatusesConstante.includes(activity.workflowStatus);
    }
    if (completionFilter === activityCompletionFilterConstante.TO_CERTIFY) {
      return activity.workflowStatus === activityWorkflowStatusConstante.VALIDATED;
    }
    if (completionFilter === activityCompletionFilterConstante.CERTIFIED) {
      return activity.workflowStatus === activityWorkflowStatusConstante.CERTIFIED;
    }
    return false;
  }

  matchesChronologyFilter(activity, chronologyFilter) {
    if (chronologyFilter === activityChronologyFilterConstante.ONGOING) {
      return activity.temporalStatus === activityTemporalStatusConstante.ONGOING;
    }
    if (chronologyFilter === activityChronologyFilterConstante.FINISHED) {
      return activity.temporalStatus === activityTemporalStatusConstante.FINISHED;
    }
    return false;
  }

  // Les deux axes se combinent en ET, sauf sous "Archivées" : une activité annulée n'a plus de
  // chronologie pertinente, l'axe est donc neutralisé côté front comme côté back (cancelled=1
  // ignore temporalStatus).
  matchesFilters(activity, completionFilter, chronologyFilter) {
    if (!this.matchesCompletionFilter(activity, completionFilter)) {
      return false;
    }
    if (completionFilter === activityCompletionFilterConstante.ARCHIVED) {
      return true;
    }
    return this.matchesChronologyFilter(activity, chronologyFilter);
  }

  // Paramètres de la route GET /contract-activity/list pour charger les activités actives
  // (DRAFT/VALIDATED/CERTIFIED, TA-4899 bis puis TA-18448) : les deux axes de filtres
  // (complétude/chronologie) restent appliqués côté client par matchesFilters(), comme l'onglet
  // Mission. CANCELLED est volontairement exclu : une activité peut avoir été soft-deleted (donc
  // réellement archivée) tout en ayant conservé un workflowStatus actif en base pour des activités
  // créées avant TA-4901 - seul le paramètre dédié `cancelled=1` (désactivant le filtre
  // softdeleteable côté repository) fait remonter fidèlement les archivées, d'où le second appel
  // séquentiel dans DashboardView.getContractActivities().
  activeStatusesListParams() {
    return { workflowStatus: activityActiveWorkflowStatusesConstante.join(",") };
  }

  filterAndSortActivities(activities, completionFilter, chronologyFilter, sort) {
    return activities
      .filter((activity) => this.matchesFilters(activity, completionFilter, chronologyFilter))
      .sort((a, b) => new Date(a[sort]) - new Date(b[sort]));
  }

  // Les deux prédicats ci-dessous partagent la même règle métier (« sortie du brouillon ») mais
  // pas le même fail-safe, volontairement : workflowStatus est garanti présent sur les deux
  // payloads consommés ici (GROUP_APPLICATION, routes /contract-activity/list et
  // /{contractActivity}/activity-evolutions), un statut absent ne survient donc que sur un objet
  // non résolu (cf. DashboardView, où l'activité fraîche peut ne pas être retrouvée dans la
  // liste). Sur ce cas indéterminé, le verrou de suppression reste fermé — action destructive,
  // miroir du 422 back — alors que le verrou d'édition reste ouvert, pour ne pas couper à tort la
  // sauvegarde progressive du wizard.
  //
  // Mirroring de la règle back (ContractActivityService::delete()) : la suppression (soft delete)
  // d'une activité n'est acceptée qu'au brouillon, tout autre statut est rejeté en 422. Prédicat
  // partagé par les deux chemins UI vers ce DELETE (corbeille de l'onglet Activité du dashboard
  // triade, archivage de l'éditeur de questionnaire) et par le garde-fou d'ouverture de la modale.
  isActivityRemovalLocked(activity) {
    return activity?.workflowStatus !== activityWorkflowStatusConstante.DRAFT;
  }

  // Prédicat partagé du verrou d'édition/signature (wizard du dashboard et du questionnaire,
  // item de liste) : une activité sortie du brouillon est signée par l'alternant, son contenu est
  // verrouillé côté back (422 sur PUT). Fail-safe ouvert, cf. commentaire ci-dessus.
  isActivityFormLocked(activity) {
    return !!activity?.workflowStatus && activity.workflowStatus !== activityWorkflowStatusConstante.DRAFT;
  }

  // Mirroring de la règle back (ActivityEvolutionService::assertValid()) : le commentaire n'est
  // requis que si la question est requise ET l'activité n'est ni terminée (temporalStatus
  // FINISHED) ni déjà validée/certifiée (workflowStatus VALIDATED/CERTIFIED) — ces deux axes sont
  // indépendants (RG2.2V2.1), d'où les deux vérifications distinctes plutôt qu'un simple
  // "!== DRAFT" qui ratait le cas terminée-mais-encore-en-brouillon.
  isActivityCommentaireMissing(activity, editedActivity, required) {
    if (!required) {
      return false;
    }
    const workflowStatus = editedActivity?.workflowStatus ?? activity.workflowStatus;
    const temporalStatus = editedActivity?.temporalStatus ?? activity.temporalStatus;
    if (
      temporalStatus === activityTemporalStatusConstante.FINISHED ||
      activityCompletedWorkflowStatusesConstante.includes(workflowStatus)
    ) {
      return false;
    }
    const commentaire = editedActivity ? editedActivity.commentaire : activity.commentaire;
    return formValidator.isEmpty(commentaire);
  }
}

export const activityViewModelManager = new ActivityViewModelManager();
